import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Baby, ClipboardCheck, IndianRupee, Sparkles, TrendingUp, Cake, PartyPopper, Send, Mail } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Legend
} from "recharts";
import { toast } from "sonner";

const STAT_CARDS = [
  { key: "total_children", label: "Children Enrolled", Icon: Baby, chip: "chip-green" },
  { key: "attendance_pct", label: "Today's Attendance", Icon: ClipboardCheck, chip: "chip-teal", suffix: "%" },
  { key: "pending_fees", label: "Pending Fees", Icon: IndianRupee, chip: "chip-pink" },
  { key: "new_enquiries", label: "New Enquiries (7d)", Icon: Sparkles, chip: "chip-amber" },
];

const CHART_COLORS = ["hsl(142 52% 43%)", "hsl(187 71% 55%)", "hsl(340 82% 70%)", "hsl(36 100% 60%)", "hsl(260 60% 65%)"];

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [birthdays, setBirthdays] = useState([]);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setData(r.data)).catch(() => {});
    api.get("/birthdays", { params: { days: 30 } }).then((r) => setBirthdays(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-8">
      <header className="animate-float-in">
        <div className="text-sm font-bold uppercase tracking-widest text-primary/80 mb-2">
          Namaste, {user?.name?.split(" ")[0]}
        </div>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight">
          Your school at a glance
        </h1>
        <p className="text-muted-foreground font-medium mt-2">
          A quick look at today — attendance, fees, and everything little.
        </p>
      </header>

      {/* Stat bento */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        {STAT_CARDS.map(({ key, label, Icon, chip, suffix }) => (
          <div
            key={key}
            data-testid={`stat-${key}`}
            className="card-soft p-5 md:p-6 animate-float-in"
          >
            <div className={`w-11 h-11 rounded-2xl ${chip} inline-flex items-center justify-center mb-4`}>
              <Icon size={20} strokeWidth={2.5} />
            </div>
            <div className="text-3xl md:text-4xl font-black tracking-tight">
              {data ? (data[key] ?? 0) : "—"}
              {suffix || ""}
            </div>
            <div className="text-xs md:text-sm font-semibold text-muted-foreground mt-1 uppercase tracking-wider">
              {label}
            </div>
          </div>
        ))}
      </div>

      {/* Birthday board */}
      <Card className="rounded-3xl border-border overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-xl font-extrabold flex items-center gap-2">
            <PartyPopper className="text-secondary-foreground" size={20} /> Birthdays coming up
          </CardTitle>
          <span className="text-xs uppercase tracking-widest font-bold text-muted-foreground">next 30 days</span>
        </CardHeader>
        <CardContent>
          {birthdays.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              <div className="text-3xl mb-2">🎂</div>
              <div className="font-medium">No birthdays this fortnight — save the confetti for later.</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {birthdays.map((b) => {
                const isToday = b.days_until === 0;
                return (
                  <div
                    key={b.child_id}
                    data-testid={`bday-${b.child_id}`}
                    className={`rounded-3xl p-4 flex items-center gap-3 border ${isToday ? "border-secondary bg-secondary/20" : "border-border bg-white"}`}
                  >
                    <div className={`w-12 h-12 rounded-2xl ${isToday ? "chip-pink" : "chip-amber"} flex items-center justify-center`}>
                      <Cake size={18} strokeWidth={2.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-extrabold truncate flex items-center gap-1">
                        {b.name} {isToday && <span className="text-lg">🎉</span>}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        Turns {b.turning} · {b.class_name} · {isToday ? "Today!" : b.days_until === 1 ? "Tomorrow" : `in ${b.days_until} days`}
                      </div>
                    </div>
                    {b.wa_link && (
                      <a
                        href={b.wa_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`wish-${b.child_id}`}
                        className="chip-green px-3 py-2 rounded-full text-xs font-bold hover:-translate-y-0.5 transition-all flex items-center gap-1"
                        title="Send birthday wish"
                      >
                        <Send size={12} /> Wish
                      </a>
                    )}
                    <button
                      data-testid={`email-bday-${b.child_id}`}
                      onClick={async () => {
                        try {
                          await api.post(`/emails/birthday/${b.child_id}`);
                          toast.success(`Birthday email sent for ${b.name}`);
                        } catch (e) {
                          toast.error(e.response?.data?.detail || "Email failed");
                        }
                      }}
                      className="chip-teal px-3 py-2 rounded-full text-xs font-bold hover:-translate-y-0.5 transition-all flex items-center gap-1"
                      title="Email birthday wish"
                    >
                      <Mail size={12} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 rounded-3xl border-border">
          <CardHeader>
            <CardTitle className="text-xl font-extrabold flex items-center gap-2">
              <TrendingUp size={20} className="text-primary" /> Fee Collection
            </CardTitle>
          </CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.fee_series || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fontWeight: 600 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <RTooltip />
                <Legend />
                <Bar dataKey="paid" fill="hsl(142 52% 55%)" radius={[10, 10, 0, 0]} name="Collected (₹)" />
                <Bar dataKey="unpaid" fill="hsl(340 82% 72%)" radius={[10, 10, 0, 0]} name="Pending (₹)" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="rounded-3xl border-border">
          <CardHeader>
            <CardTitle className="text-xl font-extrabold">Enrollment by Class</CardTitle>
          </CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data?.enrollment_by_class || []}
                  dataKey="count"
                  nameKey="class"
                  cx="50%" cy="50%"
                  innerRadius={50} outerRadius={90}
                  paddingAngle={4}
                >
                  {(data?.enrollment_by_class || []).map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <RTooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-3xl border-border">
        <CardHeader>
          <CardTitle className="text-xl font-extrabold">Enrollment trend</CardTitle>
        </CardHeader>
        <CardContent className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data?.enrollment_series || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fontWeight: 600 }} />
              <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
              <RTooltip />
              <Line type="monotone" dataKey="count" stroke="hsl(142 52% 43%)" strokeWidth={3} dot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}
